import { useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../lib/supabase";

export default function ForgotPassword() {
  const [isDark, setIsDark]   = useState(false);
  const [email, setEmail]     = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent]       = useState(false);
  const [error, setError]     = useState("");
  const [lastSent, setLastSent] = useState<number | null>(null);

  const RESEND_COOLDOWN_MS = 60_000;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) { setError("Please enter your email address."); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) { setError("Please enter a valid email address."); return; }
    if (lastSent && Date.now() - lastSent < RESEND_COOLDOWN_MS) {
      const secs = Math.ceil((RESEND_COOLDOWN_MS - (Date.now() - lastSent)) / 1000);
      setError(`Please wait ${secs} second${secs !== 1 ? "s" : ""} before requesting another link.`); return;
    }
    setLoading(true); setError("");
    const { error: err } = await supabase.auth.resetPasswordForEmail(
      email.trim().toLowerCase(),
      { redirectTo: `${window.location.origin}/reset-password` }
    );
    if (err) { setError("Could not send reset email. Please check the address and try again."); setLoading(false); return; }
    setLastSent(Date.now());
    setLoading(false);
    setSent(true);
  };

  const D = isDark;
  const pageBg    = D ? "linear-gradient(150deg,#07101f 0%,#0a1628 55%,#07101f 100%)" : "linear-gradient(150deg,#f0f4ff 0%,#e8f0fe 40%,#fef9f0 100%)";
  const formBg    = D ? "#111c2e" : "#ffffff";
  const textMain  = D ? "#f1f0ed" : "#0f172a";
  const textSub   = D ? "rgba(241,240,237,0.55)" : "#334155";
  const labelClr  = D ? "rgba(241,240,237,0.65)" : "#1e3a5f";
  const inputBg   = D ? "rgba(255,255,255,0.06)" : "#f8fafc";
  const inputBdr  = D ? "rgba(255,255,255,0.11)" : "#cbd5e1";
  const phClr     = D ? "rgba(255,255,255,0.25)" : "#94a3b8";
  const footerBdr = D ? "rgba(255,255,255,0.07)" : "#e2e8f0";
  const errBg     = D ? "rgba(220,38,38,0.09)" : "#fef2f2";
  const errBdr    = D ? "rgba(220,38,38,0.28)" : "rgba(220,38,38,0.22)";
  const errTitle  = D ? "#f87171" : "#dc2626";
  const errHint   = D ? "rgba(241,240,237,0.48)" : "#9a3412";
  const captionClr= D ? "rgba(241,240,237,0.22)" : "#94a3b8";
  const tglBg     = D ? "rgba(255,255,255,0.07)" : "rgba(15,23,42,0.06)";
  const tglBdr    = D ? "rgba(255,255,255,0.13)" : "rgba(15,23,42,0.1)";
  const tglClr    = D ? "#f1f0ed" : "#0f172a";
  const cardShadow= D ? "0 24px 64px rgba(0,0,0,0.6),0 4px 18px rgba(0,0,0,0.35)" : "0 24px 64px rgba(15,23,42,0.13),0 4px 18px rgba(15,23,42,0.07)";

  return (
    <div style={{ minHeight:"100dvh", background:pageBg, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"32px 16px 40px", fontFamily:"'DM Sans',sans-serif", transition:"background 0.35s" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@700;800&family=DM+Sans:wght@400;500;600&family=DM+Mono:wght@400;500&display=swap');
        *, *::before, *::after { box-sizing: border-box; }
        @keyframes riseUp { from{opacity:0;transform:translateY(28px)} to{opacity:1;transform:translateY(0)} }
        @keyframes spin   { to{transform:rotate(360deg)} }
        @keyframes errIn  { from{opacity:0;transform:translateY(-4px)} to{opacity:1;transform:translateY(0)} }
        .card-fp { animation: riseUp 0.5s cubic-bezier(0.22,1,0.36,1) 0.05s both; }
        .err-in  { animation: errIn 0.22s ease; }
        .tgl     { transition:all 0.2s; } .tgl:hover { opacity:0.8; }
        .own-input {
          width:100%; background:${inputBg};
          border:1.5px solid ${inputBdr}; border-radius:11px;
          padding:13px 16px 13px 44px; color:${textMain};
          font-size:15px; font-family:'DM Sans',sans-serif;
          transition:border-color 0.18s,box-shadow 0.18s;
          -webkit-appearance:none; outline:none;
        }
        .own-input:focus { border-color:#3b82f6; box-shadow:0 0 0 3px rgba(59,130,246,0.14); }
        .own-input::placeholder { color:${phClr}; }
        .own-input:disabled { opacity:0.5; cursor:not-allowed; }
        .own-btn {
          width:100%; border:none; border-radius:12px; padding:15px;
          background:linear-gradient(135deg,#1d4ed8 0%,#1e3a5f 100%);
          color:#fff; font-family:'Syne',sans-serif; font-weight:700;
          font-size:15px; letter-spacing:0.015em; cursor:pointer;
          box-shadow:0 4px 18px rgba(29,78,216,0.35);
          transition:opacity 0.18s,transform 0.12s,box-shadow 0.18s;
          display:flex; align-items:center; justify-content:center; gap:9px;
          -webkit-tap-highlight-color:transparent;
        }
        .own-btn:hover:not(:disabled) { opacity:0.88; box-shadow:0 7px 24px rgba(29,78,216,0.45); }
        .own-btn:active:not(:disabled){ transform:scale(0.98); }
        .own-btn:disabled { opacity:0.45; cursor:not-allowed; box-shadow:none; }
      `}</style>

      {/* Theme toggle */}
      <button className="tgl" onClick={() => setIsDark(d => !d)} style={{ position:"fixed", top:18, right:18, zIndex:200, background:tglBg, border:`1px solid ${tglBdr}`, borderRadius:40, padding:"7px 14px", cursor:"pointer", display:"flex", alignItems:"center", gap:6, color:tglClr, fontSize:12, fontFamily:"'DM Sans',sans-serif", fontWeight:600, backdropFilter:"blur(8px)" }}>
        {D
          ? <><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/></svg>Light</>
          : <><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>Dark</>}
      </button>

      <div className="card-fp" style={{ width:"100%", maxWidth:420, borderRadius:24, overflow:"hidden", boxShadow:cardShadow }}>

        {/* Navy gradient header */}
        <div style={{ background:"linear-gradient(145deg,#0f172a 0%,#1e3a5f 55%,#1d4ed8 100%)", padding:"28px 28px 44px", position:"relative", overflow:"hidden" }}>
          <div style={{ position:"absolute", top:-60, right:-60, width:200, height:200, borderRadius:"50%", border:"1px solid rgba(255,255,255,0.07)", pointerEvents:"none" }} />
          <div style={{ position:"absolute", top:-25, right:-25, width:110, height:110, borderRadius:"50%", border:"1px solid rgba(255,255,255,0.05)", pointerEvents:"none" }} />
          <div style={{ position:"absolute", top:22, left:28, width:6, height:6, borderRadius:"50%", background:"rgba(251,191,36,0.7)" }} />
          <div style={{ position:"absolute", top:22, left:40, width:4, height:4, borderRadius:"50%", background:"rgba(251,191,36,0.4)" }} />
          <div style={{ position:"relative" }}>
            <div style={{ fontSize:13, color:"rgba(251,191,36,0.8)", fontFamily:"'DM Mono',monospace", letterSpacing:"0.08em", marginBottom:8 }}>OWNER PORTAL</div>
            <div style={{ fontSize:"clamp(19px,3.5vw,24px)", fontFamily:"'Syne',sans-serif", fontWeight:800, color:"#fff", letterSpacing:"-0.02em", lineHeight:1.2, marginBottom:6 }}>
              🔑 Forgot Password?
            </div>
            <div style={{ fontSize:13.5, color:"rgba(255,255,255,0.55)", lineHeight:1.5 }}>
              No worries — we'll send a reset link<br/>to your email address.
            </div>
          </div>
          <div style={{ position:"absolute", bottom:-1, left:0, right:0, height:32, background:formBg, borderRadius:"50% 50% 0 0 / 100% 100% 0 0", transition:"background 0.35s" }} />
        </div>

        {/* Form body */}
        <div style={{ background:formBg, padding:"8px 28px 28px", transition:"background 0.35s" }}>
          {sent ? (
            <div style={{ textAlign:"center", padding:"16px 0" }}>
              <div style={{ fontSize:52, marginBottom:14 }}>📬</div>
              <div style={{ fontFamily:"'Syne',sans-serif", fontWeight:800, fontSize:20, color:textMain, marginBottom:8 }}>Check your inbox</div>
              <div style={{ fontSize:13.5, color:textSub, lineHeight:1.75, marginBottom:20 }}>
                A reset link was sent to<br/>
                <strong style={{ color:"#3b82f6" }}>{email}</strong>
              </div>
              <div style={{ background: D ? "rgba(251,191,36,0.07)" : "rgba(251,191,36,0.06)", border:"1px solid rgba(251,191,36,0.2)", borderRadius:10, padding:"12px 16px", marginBottom:22, fontSize:12, fontFamily:"'DM Mono',monospace", color:textSub, lineHeight:1.6 }}>
                ⚠️ Can't find it? Check your <strong style={{ color:"#f59e0b" }}>Spam</strong> folder.<br/>Link expires in <strong style={{ color:textMain }}>1 hour</strong>.
              </div>
              <Link to="/login" style={{ fontSize:13, color:"#3b82f6", fontFamily:"'DM Sans',sans-serif", fontWeight:600, textDecoration:"none" }}
                onMouseEnter={e => e.currentTarget.style.textDecoration="underline"}
                onMouseLeave={e => e.currentTarget.style.textDecoration="none"}>
                ← Back to Sign In
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} style={{ display:"flex", flexDirection:"column", gap:14, marginTop:8 }}>
              <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                <label style={{ fontSize:12.5, fontWeight:600, color:labelClr, paddingLeft:2, letterSpacing:"0.01em" }}>Email address</label>
                <div style={{ position:"relative" }}>
                  <span style={{ position:"absolute", left:14, top:"50%", transform:"translateY(-50%)", pointerEvents:"none", opacity:0.55, display:"flex" }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={D ? "#94a3b8" : "#64748b"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                  </span>
                  <input className="own-input" type="email" value={email} onChange={e => { setEmail(e.target.value); setError(""); }} placeholder="owner@yourbusiness.com" autoComplete="email" disabled={loading} />
                </div>
              </div>

              {error && (
                <div className="err-in" style={{ background:errBg, border:`1px solid ${errBdr}`, borderRadius:10, padding:"11px 14px", display:"flex", gap:10, alignItems:"flex-start" }}>
                  <span style={{ fontSize:15, flexShrink:0, marginTop:1 }}>⚠️</span>
                  <div>
                    <div style={{ color:errTitle, fontSize:13, fontWeight:600, marginBottom:2 }}>{error}</div>
                    <div style={{ color:errHint, fontSize:11, fontFamily:"'DM Mono',monospace", lineHeight:1.5 }}>Check your email address and try again.</div>
                  </div>
                </div>
              )}

              <button type="submit" disabled={loading} className="own-btn" style={{ marginTop:4 }}>
                {loading
                  ? <><span style={{ width:16, height:16, border:"2.5px solid rgba(255,255,255,0.3)", borderTopColor:"#fff", borderRadius:"50%", display:"inline-block", animation:"spin 0.7s linear infinite" }} />Sending…</>
                  : "Send Reset Link →"
                }
              </button>

              <div style={{ paddingTop:14, borderTop:`1.5px solid ${footerBdr}`, textAlign:"center" }}>
                <Link to="/login" style={{ fontSize:13, color:"#3b82f6", fontWeight:600, textDecoration:"none" }}
                  onMouseEnter={e => e.currentTarget.style.textDecoration="underline"}
                  onMouseLeave={e => e.currentTarget.style.textDecoration="none"}>
                  ← Back to Sign In
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>

      <div style={{ marginTop:16, fontSize:11, color:captionClr, fontFamily:"'DM Mono',monospace", textAlign:"center" }}>
        Epic Sales Tracker · Owner Portal
      </div>
    </div>
  );
}